import React from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MuiButton from '@mui/material/Button';
import LandingLayout from './components/LandingLayout';
import RGRGallery from './RGRGallery';

const SHADOW = '4px 4px 0px #1e1e1e';
const SHADOW_SM = '2px 2px 0px #1e1e1e';
const PRESSTO_PURPLE = '#5C4FB8';
const CREAM = '#F2EEE5';

const STATS = [
  { n: '635+', label: 'writing activities' },
  { n: '13',   label: 'programs' },
  { n: 'K–8',  label: 'grade range' },
];

const PROGRAMS = [
  { name: 'Nearpod',           color: '#16A8F9', text: '#fff' },
  { name: 'Britannica',        color: '#CC0000', text: '#fff' },
  { name: 'Amplify CKLA',      color: '#F26522', text: '#fff' },
  { name: 'Really Great Reading', color: '#C8102E', text: '#fff' },
  { name: 'BrainPop',          color: '#00AEEF', text: '#fff' },
  { name: 'EL Education',      color: '#003F7F', text: '#fff' },
  { name: 'Wit & Wisdom',      color: '#5C4FB8', text: '#fff' },
  { name: 'Benchmark Advance', color: '#2E7D32', text: '#fff' },
  { name: 'Word Generation',   color: '#6D4C41', text: '#fff' },
  { name: 'Countdown',         color: '#FF6F00', text: '#fff' },
  { name: 'Blast Foundations', color: '#1565C0', text: '#fff' },
  { name: 'HD Word',           color: '#6A1B9A', text: '#fff' },
  { name: 'Reading Horizons',  color: '#00695C', text: '#fff' },
];

function CTAButton({ children, onClick, dark = true }: { children: React.ReactNode; onClick: () => void; dark?: boolean }) {
  return (
    <MuiButton
      variant="contained"
      onClick={onClick}
      sx={{
        backgroundColor: dark ? '#2C2C2C' : '#FFE082',
        color: dark ? '#fff' : '#2C2C2C',
        fontFamily: 'Montserrat, Inter, sans-serif', fontWeight: 700, fontSize: '1rem',
        textTransform: 'none', borderRadius: '6px', border: '2px solid #1e1e1e',
        boxShadow: SHADOW, px: 4, py: 1.5,
        '&:hover': { boxShadow: SHADOW_SM, transform: 'translate(2px,2px)', backgroundColor: dark ? '#2C2C2C' : '#FFE082' },
        transition: 'all 100ms',
      }}
    >
      {children}
    </MuiButton>
  );
}

export default function CurriculumGalleryPage() {
  const navigate = useNavigate();

  return (
    <LandingLayout>

      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <Box sx={{ backgroundColor: '#fff', borderBottom: '2px solid #1e1e1e', px: { xs: 3, md: 8 }, pt: { xs: 8, md: 12 }, pb: { xs: 8, md: 10 } }}>
        <Box sx={{ maxWidth: 900, mx: 'auto', textAlign: 'center' }}>

          <Box sx={{ display: 'inline-block', backgroundColor: '#FFE082', border: '2px solid #1e1e1e', borderRadius: '4px', px: 1.5, py: 0.5, mb: 3, boxShadow: SHADOW_SM }}>
            <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Curriculum-Aligned Writing
            </Typography>
          </Box>

          <Typography variant="h1" sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: { xs: '2.4rem', md: '3.5rem' }, lineHeight: 1.1, mb: 3, color: '#1e1e1e', letterSpacing: '-0.02em' }}>
            Pressto pairs with the<br />curricula you already use.
          </Typography>

          <Typography sx={{ fontFamily: 'Lato, Inter, sans-serif', fontSize: { xs: '1.05rem', md: '1.2rem' }, color: '#444', lineHeight: 1.7, mb: 5, maxWidth: 680, mx: 'auto' }}>
            Every lesson below is a ready-to-use writing activity built around content students are already studying — phonics lessons, Nearpod slides, Britannica articles, and more. Students read (or listen, or watch) in the source curriculum, then write in Pressto.
          </Typography>

          {/* Stats */}
          <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap', mb: 6 }}>
            {STATS.map(s => (
              <Box key={s.label} sx={{ backgroundColor: CREAM, border: '2px solid #1e1e1e', borderRadius: '8px', px: 3, py: 2, boxShadow: SHADOW_SM, minWidth: 120, textAlign: 'center' }}>
                <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: '2rem', color: PRESSTO_PURPLE, lineHeight: 1 }}>{s.n}</Typography>
                <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.8rem', color: '#555', mt: 0.5 }}>{s.label}</Typography>
              </Box>
            ))}
          </Box>

          <CTAButton onClick={() => navigate('/register')}>Get started free</CTAButton>
        </Box>
      </Box>

      {/* ── Program chips ─────────────────────────────────────────────────── */}
      <Box sx={{ backgroundColor: CREAM, borderBottom: '2px solid #1e1e1e', px: { xs: 3, md: 8 }, py: { xs: 4, md: 5 } }}>
        <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
          <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#666', textAlign: 'center', mb: 3 }}>
            Programs covered
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: 'center' }}>
            {PROGRAMS.map(p => (
              <Box key={p.name} sx={{ backgroundColor: p.color, color: p.text, border: '2px solid #1e1e1e', borderRadius: '999px', px: 2, py: 0.6, boxShadow: '2px 2px 0 #1e1e1e', fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                {p.name}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ── How it works ──────────────────────────────────────────────────── */}
      <Box sx={{ backgroundColor: '#fff', borderBottom: '2px solid #1e1e1e', px: { xs: 3, md: 8 }, py: { xs: 6, md: 8 } }}>
        <Box sx={{ maxWidth: 900, mx: 'auto', textAlign: 'center' }}>
          <Typography variant="h2" sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: { xs: '1.6rem', md: '2rem' }, color: '#1e1e1e', mb: 6 }}>
            How it works
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 4 }}>
            {[
              { n: '1', title: 'Pick a lesson', body: 'Find a writing activity aligned to the unit your class is already studying — search by program, subject, or grade.' },
              { n: '2', title: 'Assign it in Pressto', body: 'One click sends students to a scaffolded Pressto workspace built around that lesson\'s topic, vocabulary, and writing plan.' },
              { n: '3', title: 'Students write, you see it all', body: 'Students draft with AI support and sentence frames. You see every student\'s progress in real time and give feedback.' },
            ].map(step => (
              <Box key={step.n} sx={{ textAlign: 'left', border: '2px solid #1e1e1e', borderRadius: '10px', p: 3.5, boxShadow: SHADOW, backgroundColor: '#fff' }}>
                <Box sx={{ width: 40, height: 40, borderRadius: '8px', backgroundColor: PRESSTO_PURPLE, border: '2px solid #1e1e1e', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                  <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: '1.1rem', color: '#fff' }}>{step.n}</Typography>
                </Box>
                <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '1rem', color: '#1e1e1e', mb: 1 }}>{step.title}</Typography>
                <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.9rem', color: '#555', lineHeight: 1.6 }}>{step.body}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ── Gallery ───────────────────────────────────────────────────────── */}
      <RGRGallery />

      {/* ── Bottom CTA ────────────────────────────────────────────────────── */}
      <Box sx={{ backgroundColor: '#1e1e1e', px: { xs: 3, md: 8 }, py: { xs: 8, md: 10 }, textAlign: 'center' }}>
        <Typography variant="h2" sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#fff', mb: 2, letterSpacing: '-0.01em' }}>
          Don&apos;t see your curriculum?
        </Typography>
        <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '1.1rem', color: 'rgba(255,255,255,0.75)', mb: 5, maxWidth: 560, mx: 'auto', lineHeight: 1.7 }}>
          We&apos;re building new alignments every week. Tell us what you&apos;re using and we&apos;ll prioritize it.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <CTAButton onClick={() => navigate('/register')}>Sign up free</CTAButton>
          <CTAButton dark={false} onClick={() => navigate('/contact')}>Request a curriculum</CTAButton>
        </Box>
      </Box>

    </LandingLayout>
  );
}
